// sheetFile.ts · v12.10.17 (#1746 Import patients: read a .csv or .xlsx in the browser - no new dependency) · 2026-10-16 · VoiceSpectrum AI
//
// CSV: RFC 4180 quotes, CRLF / LF, a BOM, comma / semicolon / tab (picked from the header line).
// XLSX: the file is a ZIP; the browser's own DecompressionStream('deflate-raw') unpacks it, DOMParser reads the first
// worksheet and the shared strings. Cells formatted as dates come back as MM/DD/YYYY (Excel keeps dates as day numbers).
// Formulas give their last saved value. Older .xls (binary) files are not read - save them as .xlsx or .csv.

/** rowNos[i] is the spreadsheet row number of rows[i] (header = 1) - blank rows are dropped, the numbers stay true. */
export type Sheet = { headers: string[]; rows: string[][]; rowNos: number[] };

/** Reads the file by its extension. Throws an Error with a sentence for the person when it can't. */
export async function readSheetFile(file: File): Promise<Sheet> {
  const name = file.name.toLowerCase();
  if (name.endsWith('.csv') || name.endsWith('.txt')) return toSheet(parseCsv(await file.text()));
  if (name.endsWith('.xlsx')) return toSheet(await readXlsx(await file.arrayBuffer()));
  if (name.endsWith('.xls')) throw new Error('This is an old Excel file (.xls). Save it as .xlsx or .csv and choose it again.');
  throw new Error('Choose a .xlsx or .csv file.');
}

function toSheet(grid: string[][]): Sheet {
  const kept = grid.map((r, i) => ({ r: r ?? [], n: i + 1 })).filter((x) => x.r.some((c) => (c ?? '').trim() !== ''));
  if (kept.length === 0) return { headers: [], rows: [], rowNos: [] };
  const width = Math.max(...kept.map((x) => x.r.length));
  const headers = Array.from({ length: width }, (_, i) => (kept[0].r[i] ?? '').trim() || `Column ${i + 1}`);
  const body = kept.slice(1);
  return { headers, rows: body.map((x) => Array.from({ length: width }, (_, i) => (x.r[i] ?? '').trim())), rowNos: body.map((x) => x.n) };
}

// ── CSV ─────────────────────────────────────────────────────────────────────────────────────────────────────────
export function parseCsv(text: string): string[][] {
  const t = text.replace(/^﻿/, '');
  const firstLine = t.slice(0, Math.max(0, t.search(/\r?\n/)) || t.length);
  const count = (ch: string) => firstLine.split(ch).length - 1;
  const delim = [',', ';', '\t'].reduce((best, ch) => (count(ch) > count(best) ? ch : best), ',');
  const out: string[][] = [];
  let row: string[] = []; let cell = ''; let q = false;
  for (let i = 0; i < t.length; i++) {
    const ch = t[i];
    if (q) {
      if (ch === '"') { if (t[i + 1] === '"') { cell += '"'; i++; } else q = false; }
      else cell += ch;
    } else if (ch === '"' && cell === '') q = true;
    else if (ch === delim) { row.push(cell); cell = ''; }
    else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && t[i + 1] === '\n') i++;
      row.push(cell); out.push(row); row = []; cell = '';
    } else cell += ch;
  }
  if (cell !== '' || row.length > 0) { row.push(cell); out.push(row); }
  return out;
}

/** One CSV line - quoted when it has a comma, quote or line break. */
export function csvLine(cells: (string | null | undefined)[]): string {
  return cells.map((c) => {
    const v = c ?? '';
    return /[",\r\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
  }).join(',');
}

// ── XLSX ────────────────────────────────────────────────────────────────────────────────────────────────────────
type ZipEntry = { name: string; method: number; size: number; offset: number };

function zipEntries(buf: ArrayBuffer): ZipEntry[] {
  const v = new DataView(buf);
  let eocd = -1;
  for (let i = buf.byteLength - 22; i >= Math.max(0, buf.byteLength - 66000); i--) if (v.getUint32(i, true) === 0x06054b50) { eocd = i; break; }
  if (eocd < 0) throw new Error('This file is not a readable .xlsx.');
  const n = v.getUint16(eocd + 10, true);
  let p = v.getUint32(eocd + 16, true);
  const dec = new TextDecoder();
  const out: ZipEntry[] = [];
  for (let k = 0; k < n; k++) {
    if (v.getUint32(p, true) !== 0x02014b50) break;
    const method = v.getUint16(p + 10, true);
    const size = v.getUint32(p + 20, true);
    const nameLen = v.getUint16(p + 28, true), extraLen = v.getUint16(p + 30, true), commentLen = v.getUint16(p + 32, true);
    const local = v.getUint32(p + 42, true);
    const name = dec.decode(new Uint8Array(buf, p + 46, nameLen));
    const lName = v.getUint16(local + 26, true), lExtra = v.getUint16(local + 28, true);
    out.push({ name, method, size, offset: local + 30 + lName + lExtra });
    p += 46 + nameLen + extraLen + commentLen;
  }
  return out;
}

async function unzipText(buf: ArrayBuffer, e: ZipEntry): Promise<string> {
  const data = new Uint8Array(buf, e.offset, e.size);
  if (e.method === 0) return new TextDecoder().decode(data);
  if (e.method !== 8) throw new Error('This .xlsx uses a compression the browser cannot read - save it as .csv.');
  if (typeof DecompressionStream === 'undefined') throw new Error('This browser cannot open .xlsx files - save the file as .csv and choose it again.');
  const stream = new Blob([data]).stream().pipeThrough(new DecompressionStream('deflate-raw'));
  return await new Response(stream).text();
}

const byTag = (n: Document | Element, tag: string): Element[] => Array.from(n.getElementsByTagNameNS('*', tag));
const BUILTIN_DATE = new Set([14, 15, 16, 17, 18, 19, 20, 21, 22, 27, 30, 36, 45, 46, 47, 50, 57]);

async function readXlsx(buf: ArrayBuffer): Promise<string[][]> {
  const entries = zipEntries(buf);
  const get = (name: string) => entries.find((e) => e.name.toLowerCase() === name.toLowerCase());
  const xml = async (name: string) => { const e = get(name); return e ? new DOMParser().parseFromString(await unzipText(buf, e), 'application/xml') : null; };

  // the FIRST sheet of the workbook (its order, not the file name)
  let sheetPath = 'xl/worksheets/sheet1.xml';
  const wb = await xml('xl/workbook.xml');
  const date1904 = !!wb && byTag(wb, 'workbookPr').some((x) => ['1', 'true'].includes(x.getAttribute('date1904') ?? ''));
  const first = wb ? byTag(wb, 'sheet')[0] : undefined;
  const rid = first?.getAttribute('r:id') ?? first?.getAttributeNS('http://schemas.openxmlformats.org/officeDocument/2006/relationships', 'id');
  const rels = await xml('xl/_rels/workbook.xml.rels');
  const target = rid && rels ? byTag(rels, 'Relationship').find((r) => r.getAttribute('Id') === rid)?.getAttribute('Target') : null;
  if (target) sheetPath = target.startsWith('/') ? target.slice(1) : 'xl/' + target.replace(/^\.\//, '');
  if (!get(sheetPath)) sheetPath = entries.map((e) => e.name).filter((n) => /^xl\/worksheets\/[^/]+\.xml$/i.test(n)).sort()[0] ?? sheetPath;
  const sheet = await xml(sheetPath);
  if (!sheet) throw new Error('This .xlsx has no worksheet.');

  const ss = await xml('xl/sharedStrings.xml');
  const shared = ss ? byTag(ss, 'si').map((si) => byTag(si, 't').map((t) => t.textContent ?? '').join('')) : [];

  // which cell styles are dates
  const styles = await xml('xl/styles.xml');
  const customDate = new Set<number>();
  const dateStyle: boolean[] = [];
  if (styles) {
    for (const f of byTag(styles, 'numFmt')) {
      const code = (f.getAttribute('formatCode') ?? '').toLowerCase().replace(/"[^"]*"|\[[^\]]*\]/g, '');
      if (/[dmy]/.test(code) && !/^general$/.test(code)) customDate.add(Number(f.getAttribute('numFmtId')));
    }
    const xfs = byTag(styles, 'cellXfs')[0];
    if (xfs) for (const xf of byTag(xfs, 'xf')) { const id = Number(xf.getAttribute('numFmtId') ?? 0); dateStyle.push(BUILTIN_DATE.has(id) || customDate.has(id)); }
  }

  const grid: string[][] = [];
  for (const row of byTag(sheet, 'row')) {
    const r = Number(row.getAttribute('r') ?? grid.length + 1) - 1;
    const cells: string[] = [];
    for (const c of byTag(row, 'c')) {
      const ref = c.getAttribute('r') ?? '';
      const col = colIndex(ref.replace(/\d+/g, '')) ?? cells.length;
      const t = c.getAttribute('t');
      const v = byTag(c, 'v')[0]?.textContent ?? '';
      let val: string;
      if (t === 's') val = shared[Number(v)] ?? '';
      else if (t === 'inlineStr') val = byTag(c, 't').map((x) => x.textContent ?? '').join('');
      else if (t === 'b') val = v === '1' ? 'TRUE' : 'FALSE';
      else if (t === 'str' || t === 'e') val = v;
      else {
        const sIdx = Number(c.getAttribute('s') ?? -1);
        val = v !== '' && sIdx >= 0 && dateStyle[sIdx] ? serialToDate(Number(v), date1904) : numberText(v);
      }
      cells[col] = val;
    }
    grid[r] = Array.from({ length: cells.length }, (_, i) => cells[i] ?? '');
  }
  return Array.from({ length: grid.length }, (_, i) => grid[i] ?? []);
}

function colIndex(letters: string): number | null {
  if (!letters) return null;
  let n = 0;
  for (const ch of letters.toUpperCase()) n = n * 26 + (ch.charCodeAt(0) - 64);
  return n - 1;
}

/** 9045550110 stays 9045550110 (never 9.04555E+09); 7083.0 -> 7083. */
function numberText(v: string): string {
  if (!/^-?\d+(\.\d+)?(e[+-]?\d+)?$/i.test(v)) return v;
  const n = Number(v);
  return Number.isInteger(n) ? n.toFixed(0) : String(n);
}

/** Excel day number -> MM/DD/YYYY (1900 system with its Feb-29-1900 quirk, or the 1904 system). */
export function serialToDate(serial: number, date1904 = false): string {
  if (!Number.isFinite(serial) || serial <= 0) return String(serial);
  const days = Math.floor(serial) + (date1904 ? 1462 : 0);
  const ms = Date.UTC(1899, 11, 30) + days * 86400000;
  const d = new Date(ms);
  return `${String(d.getUTCMonth() + 1).padStart(2, '0')}/${String(d.getUTCDate()).padStart(2, '0')}/${d.getUTCFullYear()}`;
}
